import React from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import classNames from 'classnames';
import routePaths from '../../routePaths';
import { pathByRouteName } from '../../util/routes';

/**
 * A Link that takes a route *name* instead of a path, so paths live in one
 * place (routePaths.js) and a rename cannot leave a dead link behind.
 *
 * It used to preload the target page on hover, but routePaths carries no
 * component reference and the pages are no longer lazy-loaded, so the
 * handler could never find anything to preload. It also took a `params`
 * prop to fill route parameters; no route has any — the service routes are
 * eight literal paths generated from config, not one parameterised path.
 *
 * Anything else passed is forwarded to the underlying anchor. It used not to
 * be, which silently dropped the `data-reveal` attributes on every card that
 * is a link: useScrollReveal collects `[data-reveal]` nodes, so those cards
 * were simply left out of the reveal animation. withRouter's own injected
 * props are destructured away first — spreading `history`, `location` or
 * `staticContext` onto a DOM node is a React warning and invalid HTML.
 */
export const NamedLinkComponent = props => {
  const {
    name,
    to = {},
    children = null,
    className = '',
    style = {},
    activeClassName = 'NamedLink_active',
    title = null,
    match = {},
    history,
    location,
    staticContext,
    ...rest
  } = props;

  const pathname = pathByRouteName(name, routePaths);
  const active = match.url && match.url === pathname;

  return (
    <Link
      to={{ pathname, ...to }}
      className={classNames(className, { [activeClassName]: active })}
      style={style}
      title={title}
      {...rest}
    >
      {children}
    </Link>
  );
};

NamedLinkComponent.displayName = 'NamedLink';

const { object, string, shape, any } = PropTypes;

NamedLinkComponent.propTypes = {
  name: string.isRequired,
  to: shape({ search: string, hash: string, state: object }),
  children: any,
  className: string,
  style: object,
  activeClassName: string,
  title: string,
  match: object,
};

const NamedLink = withRouter(NamedLinkComponent);
NamedLink.displayName = 'NamedLink';

export default NamedLink;
